import { useState } from 'react';
import { Spinner } from '@/components/icons';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { getAnamnesisWordCount } from '@/pages/patients/anamnesis/utils';

type Props = {
    /** Seeds the editor — the current anamnesis, including text extracted from a file. */
    initialHtml: string;
    saving: boolean;
    /** Persistent rejection (content too long, plan limit); a toast would scroll away. */
    error: string | null;
    onSave: (html: string) => void;
};

/**
 * Pasting a ready anamnesis straight into the report's "Gerenciar anamnese" modal,
 * for the psychologist who already wrote it in Word or Google Docs.
 *
 * Purely presentational: the owner holds the mutation and decides what happens
 * after a save, so this view only knows the document it is editing. Leaving is
 * the owner's business too — it renders the dialog header this view sits under.
 */
export default function AnamnesisPasteView({ initialHtml, saving, error, onSave }: Props) {
    const [html, setHtml] = useState(initialHtml);

    // TipTap normalizes an empty document to '<p></p>', so a length check would
    // read as content. The word count is the same measure the summary card uses.
    const isEmpty = getAnamnesisWordCount(html) === 0;

    return (
        <div className="space-y-4">
            <div className="overflow-hidden rounded-lg border border-border bg-card">
                <RichTextEditor
                    value={html}
                    onChange={setHtml}
                    disabled={saving}
                    placeholder="Cole ou escreva a anamnese deste paciente…"
                    showWordCount
                    enableBubbleMenu
                    // The provider only autofocuses when the dialog opens, not when
                    // this view replaces the previous one — so claim the caret here
                    // and the paste can go straight in.
                    autoFocus
                    // An anamnesis runs to hundreds of words, so the editor
                    // opens on a page rather than on a box the writer has to
                    // scroll from the second paragraph on.
                    viewportClassName="max-h-[60vh] min-h-[420px]"
                    editorClassName="dark:prose-invert px-4 py-3"
                    className="border-0 bg-transparent shadow-none"
                />
            </div>

            {error && <Alert variant="destructive">{error}</Alert>}

            {/* Only the commit lives here — the way back is the header's back
                control, where every other dialog in the product keeps it. */}
            <div className="flex justify-end border-t border-border pt-4">
                <Button type="button" onClick={() => onSave(html)} disabled={saving || isEmpty}>
                    {saving ? (
                        <>
                            <Spinner className="animate-spin" />
                            Salvando…
                        </>
                    ) : (
                        'Salvar anamnese'
                    )}
                </Button>
            </div>
        </div>
    );
}
